import { useEffect, useState } from "react";

import {
  parseAsInteger,
  parseAsJson,
  parseAsString,
  useQueryState,
} from "nuqs";
import { z } from "zod";

import { DataTable } from "@/components/data-table/data-table";
import { DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  QueryPaginatedProgramComponentParticipantsOrderBy,
  useProgramComponentParticipantsQueryLazyQuery,
} from "@/graphql";
import { useDataTable } from "@/hooks/use-data-table";
import {
  MainComponent,
  MainProgram,
  ParticipantTableField,
} from "@/types/programs";
import { HeaderConfig } from "@/types/tables";

import { EmptyParticipant } from "./EmptyParticipant";
import {
  Participant,
  useParticipantTableColumns,
} from "./participant-table-utils";
import { SendNudgeDialog } from "./SendNudgeDialog";

export interface ParticipantHeaderConfig extends HeaderConfig {
  field: ParticipantTableField;
}

interface ProgramParticipantsTableProps {
  program: MainProgram;
  component?: MainComponent;
  onRemoveParticipant?: (participant: Participant) => void;
}

const SortMap = {
  name: QueryPaginatedProgramComponentParticipantsOrderBy.UserNameAsc,
  completedAt:
    QueryPaginatedProgramComponentParticipantsOrderBy.CompletedAtDesc,
  "-name": QueryPaginatedProgramComponentParticipantsOrderBy.UserNameDesc,
  "-completedAt":
    QueryPaginatedProgramComponentParticipantsOrderBy.CompletedAtAsc,
  isCompleted:
    QueryPaginatedProgramComponentParticipantsOrderBy.IsCompletedDesc,
  "-isCompleted":
    QueryPaginatedProgramComponentParticipantsOrderBy.IsCompletedAsc,
  totalCreditsUsed:
    QueryPaginatedProgramComponentParticipantsOrderBy.TotalCreditsUsedDesc,
  "-totalCreditsUsed":
    QueryPaginatedProgramComponentParticipantsOrderBy.TotalCreditsUsedAsc,
};

export function ProgramComponentParticipantsTable({
  program,
  component,
}: ProgramParticipantsTableProps) {
  const [fetch, { data, loading }] =
    useProgramComponentParticipantsQueryLazyQuery({
      fetchPolicy: "no-cache",
    });

  const participants = (data?.programComponentParticipants?.participants ||
    []) as Participant[];

  const [nameSearch] = useQueryState("name", parseAsString.withDefault(""));
  const [page] = useQueryState("page", parseAsInteger.withDefault(1));
  const [size] = useQueryState("perPage", parseAsInteger.withDefault(5));
  const sortSchema = z.array(
    z.object({
      id: z.string(),
      desc: z.boolean(),
    }),
  );

  const [sort] = useQueryState("sort", parseAsJson(sortSchema.parse));

  const refetchParticipants = () => {
    const currentSort = sort?.length && sort.length > 0 ? sort[0] : null;
    const orderField = currentSort
      ? `${currentSort.desc ? "-" : ""}${currentSort.id}`
      : undefined;
    fetch({
      variables: {
        programComponentId: component?.id || "",
        page: page,
        size: size,
        orderField:
          SortMap[orderField as keyof typeof SortMap] ||
          QueryPaginatedProgramComponentParticipantsOrderBy.UserNameAsc,
        userNameSearch: nameSearch,
      },
    });
  };

  useEffect(() => {
    refetchParticipants();
  }, [page, size, nameSearch, sort]);

  const [nudgeParticipant, setNudgeToParticipant] = useState<Participant>();

  const onNudgeClick = (participant: Participant) => {
    setNudgeToParticipant(participant);
  };

  const { columns } = useParticipantTableColumns({
    component,
    program,
    onNudgeClick,
  });

  const { table } = useDataTable({
    data: participants as Participant[],
    columns,
    pageCount: data?.programComponentParticipants?.totalPages || 1,
    enableHiding: false,
    getRowId: (row) => row?.id || "",
    initialState: {
      pagination: {
        pageIndex: page - 1,
        pageSize: size || 5,
      },
      columnPinning: { right: ["actions"] },
    },
  });

  const loadingSkeleton = (
    <div className="flex flex-col gap-4 h-full w-full">
      {Array.from({ length: size }).map((_, index) => (
        <Skeleton key={index} className="h-10 w-full" />
      ))}
    </div>
  );

  return (
    <>
      {nudgeParticipant && component && (
        <SendNudgeDialog
          open={!!nudgeParticipant}
          onClose={() => {
            setNudgeToParticipant(undefined);
          }}
          participant={nudgeParticipant}
          component={component}
          onFinished={() => {
            setNudgeToParticipant(undefined);
            refetchParticipants();
          }}
        />
      )}
      <DataTable
        table={table}
        className="overflow-visible"
        totalRecords={data?.programComponentParticipants?.totalParticipants}
        emptyState={
          loading ? (
            loadingSkeleton
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              {nameSearch ? (
                <div className="flex flex-col items-center justify-center text-gray-500">
                  No participants found. Please try again with different search
                  criteria.
                </div>
              ) : (
                <EmptyParticipant programId={program.id} />
              )}
            </div>
          )
        }
      >
        <DataTableToolbar table={table}></DataTableToolbar>
      </DataTable>
    </>
  );
}
